import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FileUp, Search, Calendar, Users, UserCircle, UserIcon, X, Download, Check, Info, Eye, ClipboardList } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card } from '../BossUI';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { useAuth } from '../../contexts/AuthContext';
import { SERVICE_TYPES, SESSION_LENGTHS } from '../../utils/constants';

// Document types that can be automatically processed
const DOCUMENT_TYPES = [
  { value: 'Initial Assessment', label: 'Initial Assessment Form' },
  { value: '12 Week Review', label: '12 Week Review Form' },
  { value: 'Session Notes', label: 'Session Notes' },
  { value: 'Student Memo', label: 'Student Memo' },
  { value: 'Other', label: 'Other Document' }
];

// Single file upload component for specific document types
const SingleFileUpload = ({ 
  file, 
  onFileChange, 
  documentType, 
  label, 
  required = false,
  acceptTypes = ".pdf,.doc,.docx"
}) => {
  const inputId = `file-upload-${documentType.replace(/\s+/g, '-').toLowerCase()}`;
  const [isDragging, setIsDragging] = useState(false);
  
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      onFileChange({
        file: selectedFile,
        type: documentType,
        id: Math.random().toString(36).substr(2, 9)
      });
    }
  };

  const handleRemove = () => {
    onFileChange(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      onFileChange({
        file: droppedFile,
        type: documentType,
        id: Math.random().toString(36).substr(2, 9)
      });
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-slate-700">
        {label}
        {required && <span className="text-red-500">*</span>}
      </label>
      
      {!file ? (
        <div
          className={`flex justify-center rounded-md border-2 border-dashed px-6 py-4 transition-colors ${
            isDragging ? 'border-blue-400 bg-blue-50' : 'border-slate-300 bg-white'
          }`}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="text-center">
            <FileUp className={`mx-auto h-8 w-8 ${isDragging ? 'text-blue-400' : 'text-slate-400'}`} />
            <div className="mt-2 flex text-sm leading-6 text-slate-600">
              <label
                htmlFor={inputId}
                className="relative cursor-pointer rounded-md font-semibold text-blue-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2 hover:text-blue-500"
              >
                <span>Upload {label}</span>
                <input
                  id={inputId}
                  name={inputId}
                  type="file"
                  className="sr-only"
                  accept={acceptTypes}
                  onChange={handleFileChange}
                />
              </label>
              <p className="pl-1">or drag and drop</p>
            </div>
            <p className="text-xs leading-5 text-slate-500">PDF, DOC, DOCX up to 16MB</p>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-md border border-green-300">
          <div className="flex items-center space-x-3">
            <Check className="h-5 w-5 text-green-500" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">
                {file.file.name}
              </p>
              <p className="text-xs text-slate-500">
                {(file.file.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleRemove}
            className="text-red-500 hover:text-red-700 p-1"
            title="Remove file"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

// Multi-file upload component with type selection (for general use)
const FileUploadWithType = ({ files, onFilesChange, serviceType }) => {
  const [isDragging, setIsDragging] = useState(false);

  const processFiles = (rawFiles) => {
    const filesWithTypes = Array.from(rawFiles).map(file => ({
      file,
      type: getDefaultFileType(file.name, serviceType),
      id: Math.random().toString(36).substr(2, 9)
    }));
    onFilesChange([...files, ...filesWithTypes]);
  };

  const handleFileChange = (e) => {
    processFiles(e.target.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files?.length) {
      processFiles(e.dataTransfer.files);
    }
  };

  const getDefaultFileType = (filename, serviceType) => {
    const name = filename.toLowerCase();
    
    // Auto-detect file types based on filename patterns
    if (name.includes('initial') || name.includes('assessment')) {
      return 'Initial Assessment';
    } else if (name.includes('12') || name.includes('twelve') || name.includes('review')) {
      return '12 Week Review';
    } else if (name.includes('memo')) {
      return 'Student Memo';
    } else if (name.includes('notes') || name.includes('session')) {
      return 'Session Notes';
    }
    
    // Default based on service type
    if (serviceType === '12 Week Review') {
      return '12 Week Review';
    }
    
    return 'Other';
  };

  const updateFileType = (fileId, newType) => {
    const updatedFiles = files.map(fileItem => 
      fileItem.id === fileId ? { ...fileItem, type: newType } : fileItem
    );
    onFilesChange(updatedFiles);
  };

  const removeFile = (fileId) => {
    const updatedFiles = files.filter(fileItem => fileItem.id !== fileId);
    onFilesChange(updatedFiles);
  };

  return (
    <div className="space-y-4">
      {/* File Upload Area */}
      <div
        className={`flex justify-center rounded-md border-2 border-dashed px-6 py-4 transition-colors ${
          isDragging ? 'border-blue-400 bg-blue-50' : 'border-slate-300 bg-white'
        }`}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="text-center">
          <FileUp className={`mx-auto h-12 w-12 ${isDragging ? 'text-blue-400' : 'text-slate-400'}`} />
          <div className="mt-4 flex text-sm leading-6 text-slate-600">
            <label
              htmlFor="file-upload-general"
              className="relative cursor-pointer rounded-md font-semibold text-blue-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2 hover:text-blue-500"
            >
              <span>Upload files</span>
              <input
                id="file-upload-general"
                name="files"
                type="file"
                className="sr-only"
                multiple
                accept=".pdf,.doc,.docx"
                onChange={handleFileChange}
              />
            </label>
            <p className="pl-1">or drag and drop</p>
          </div>
          <p className="text-xs leading-5 text-slate-600">PDF, DOC, DOCX up to 16MB each</p>
        </div>
      </div>

      {/* Uploaded Files List */}
      {files.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-slate-700">Uploaded Files</h4>
          <div className="space-y-2">
            {files.map((fileItem) => (
              <div key={fileItem.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-md border">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">
                    {fileItem.file.name}
                  </p>
                  <p className="text-xs text-slate-500">
                    {(fileItem.file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                
                <div className="flex items-center space-x-3 ml-4">
                  <select
                    value={fileItem.type}
                    onChange={(e) => updateFileType(fileItem.id, e.target.value)}
                    className="text-sm border border-slate-300 rounded-md px-2 py-1 min-w-0"
                    style={{ minWidth: '140px' }}
                  >
                    {DOCUMENT_TYPES.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  
                  <button
                    type="button"
                    onClick={() => removeFile(fileItem.id)}
                    className="text-red-500 hover:text-red-700 text-sm font-medium"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Specialized upload component for Initial Assessment (separate fields)
const InitialAssessmentUpload = ({ assessmentFile, memoFile, onAssessmentChange, onMemoChange }) => {
  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
        <div className="flex items-start">
          <Info className="h-5 w-5 text-blue-400 mt-0.5" />
          <div className="ml-3">
            <h4 className="text-sm font-medium text-blue-800">Required Documents for Initial Assessment</h4>
            <p className="mt-1 text-sm text-blue-700">
              Please upload both the Initial Assessment Form and the Student Memo in their designated fields below.
            </p>
          </div>
        </div>
      </div>

      <SingleFileUpload
        file={assessmentFile}
        onFileChange={onAssessmentChange}
        documentType="Initial Assessment"
        label="Initial Assessment Form"
        required={true}
        acceptTypes=".docx"
      />

      <SingleFileUpload
        file={memoFile}
        onFileChange={onMemoChange}
        documentType="Student Memo"
        label="Student Memo"
        required={true}
      />
    </div>
  );
};

// Specialized upload component for 12 Week Review (single required field)
const TwelveWeekReviewUpload = ({ reviewFile, onReviewChange }) => {
  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
        <div className="flex items-start">
          <Info className="h-5 w-5 text-blue-400 mt-0.5" />
          <div className="ml-3">
            <h4 className="text-sm font-medium text-blue-800">Required Document for 12 Week Review</h4>
            <p className="mt-1 text-sm text-blue-700">
              Please upload the 12 Week Review Form below.
            </p>
          </div>
        </div>
      </div>

      <SingleFileUpload
        file={reviewFile}
        onFileChange={onReviewChange}
        documentType="12 Week Review"
        label="12 Week Review Form"
        required={true}
        acceptTypes=".docx"
      />
    </div>
  );
};

// --- Toggle / Slider Checkbox ------------------------------------------------
const ToggleSwitch = ({ id, checked, onChange, label, required = false }) => (
  <div className="flex items-center space-x-3">
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
        checked ? 'bg-blue-600' : 'bg-slate-200'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
    <label htmlFor={id} className="text-sm text-slate-700 cursor-pointer" onClick={() => onChange(!checked)}>
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
  </div>
);

// --- Conditional fields per service type -------------------------------------

const InitialAssessmentFields = ({ values, onChange }) => (
  <div className="space-y-5 p-4 bg-blue-50 border border-blue-200 rounded-md">
    <h4 className="text-sm font-semibold text-blue-900 flex items-center gap-2">
      <ClipboardList className="h-4 w-4" /> Initial Assessment — Required Fields
    </h4>
    <div>
      <label className="block text-sm font-medium text-slate-700">
        Requested Semester Activities <span className="text-red-500">*</span>
      </label>
      <select
        value={values.requestedSemesterActivities}
        onChange={(e) => onChange('requestedSemesterActivities', e.target.value)}
        className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        required
      >
        <option value="">Select activities</option>
        <option value="Lessons">Lessons</option>
        <option value="Therapy">Therapy</option>
        <option value="Both">Both</option>
      </select>
    </div>
    <div>
      <label className="block text-sm font-medium text-slate-700">
        Instrument Chosen <span className="text-red-500">*</span>
      </label>
      <input
        type="text"
        value={values.instrumentChosen}
        onChange={(e) => onChange('instrumentChosen', e.target.value)}
        placeholder="e.g., Guitar, Piano, Violin"
        className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        required
      />
    </div>
    <ToggleSwitch
      id="ia-needs-instrument"
      checked={values.studentNeedsInstrument}
      onChange={(val) => onChange('studentNeedsInstrument', val)}
      label="Student Needs an Instrument"
      required
    />
  </div>
);

const TwelveWeekReviewFields = ({ values, onChange }) => (
  <div className="space-y-5 p-4 bg-purple-50 border border-purple-200 rounded-md">
    <h4 className="text-sm font-semibold text-purple-900 flex items-center gap-2">
      <ClipboardList className="h-4 w-4" /> 12 Week Review — Optional Fields
    </h4>
    <div>
      <label className="block text-sm font-medium text-slate-700">Next Semester Option for Lessons</label>
      <select
        value={values.nextSemesterLessons}
        onChange={(e) => onChange('nextSemesterLessons', e.target.value)}
        className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
      >
        <option value="">— Select —</option>
        <option value="Continue">Continue</option>
        <option value="Continue Alumni">Continue Alumni</option>
        <option value="Not Continue">Not Continue</option>
      </select>
    </div>
    <div>
      <label className="block text-sm font-medium text-slate-700">Next Semester Option for Therapy</label>
      <select
        value={values.nextSemesterTherapy}
        onChange={(e) => onChange('nextSemesterTherapy', e.target.value)}
        className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
      >
        <option value="">— Select —</option>
        <option value="Continue">Continue</option>
        <option value="Continue Alumni">Continue Alumni</option>
        <option value="Not Continue">Not Continue</option>
      </select>
    </div>
    <ToggleSwitch
      id="twr-change-instruments"
      checked={values.changeInstruments}
      onChange={(val) => onChange('changeInstruments', val)}
      label="Change Instruments"
    />
    {values.changeInstruments && (
      <div className="ml-4 space-y-4 border-l-2 border-purple-300 pl-4">
        <div>
          <label className="block text-sm font-medium text-slate-700">New Instrument</label>
          <input
            type="text"
            value={values.newInstrument}
            onChange={(e) => onChange('newInstrument', e.target.value)}
            placeholder="e.g., Drums, Bass Guitar"
            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
        </div>
        <ToggleSwitch
          id="twr-needs-instrument"
          checked={values.needsNewInstrument}
          onChange={(val) => onChange('needsNewInstrument', val)}
          label="Student Needs the New Instrument"
        />
      </div>
    )}
    <ToggleSwitch
      id="twr-request-third-semester"
      checked={values.requestThirdSemester}
      onChange={(val) => onChange('requestThirdSemester', val)}
      label="Request Third Semester"
    />
    {values.requestThirdSemester && (
      <div className="ml-4 border-l-2 border-purple-300 pl-4">
        <p className="text-xs text-purple-700 font-medium">
          ? The manager will be notified of this third semester request.
        </p>
      </div>
    )}
  </div>
);

const SongwritingAssessmentFields = ({ values, onChange }) => (
  <div className="space-y-5 p-4 bg-amber-50 border border-amber-200 rounded-md">
    <h4 className="text-sm font-semibold text-amber-900 flex items-center gap-2">
      <ClipboardList className="h-4 w-4" /> Songwriting Assessment — Required Fields
    </h4>
    <div>
      <label className="block text-sm font-medium text-slate-700">
        Songwriter Gender Preference <span className="text-red-500">*</span>
      </label>
      <select
        value={values.songwriterGenderPreference}
        onChange={(e) => onChange('songwriterGenderPreference', e.target.value)}
        className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        required
      >
        <option value="">Select preference</option>
        <option value="Male">Male</option>
        <option value="Female">Female</option>
        <option value="None">None</option>
      </select>
    </div>
    <div>
      <label className="block text-sm font-medium text-slate-700">
        Preferred Genre <span className="text-red-500">*</span>
      </label>
      <input
        type="text"
        value={values.preferredGenre}
        onChange={(e) => onChange('preferredGenre', e.target.value)}
        placeholder="e.g., Country, Rock, Jazz"
        className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        required
      />
    </div>
  </div>
);

const SessionFields = ({ values, onChange }) => (
  <div className="space-y-5 p-4 bg-slate-50 border border-slate-200 rounded-md">
    <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
      <ClipboardList className="h-4 w-4" /> Session — Optional Fields
    </h4>
    <ToggleSwitch
      id="session-ncns"
      checked={values.noCallNoShow}
      onChange={(val) => onChange('noCallNoShow', val)}
      label="No Call / No Show"
    />
  </div>
);

// --- Default field state per service type ------------------------------------
const defaultServiceTypeFields = {
  'Initial Assessment': {
    requestedSemesterActivities: '',
    instrumentChosen: '',
    studentNeedsInstrument: false,
  },
  '12 Week Review': {
    nextSemesterLessons: '',
    nextSemesterTherapy: '',
    changeInstruments: false,
    newInstrument: '',
    needsNewInstrument: false,
    requestThirdSemester: false,
  },
  'Songwriting Assessment': {
    songwriterGenderPreference: '',
    preferredGenre: '',
  },
  'Session': {
    noCallNoShow: false,
  },
};

// --- Main Create Session Report component ------------------------------------
const CreateSessionReport = () => {
  // Separate state for Initial Assessment files
  const [assessmentFile, setAssessmentFile] = useState(null);
  const [memoFile, setMemoFile] = useState(null);
  
  // Separate state for 12 Week Review file
  const [reviewFile, setReviewFile] = useState(null);
  
  // General files for other service types
  const [selectedFiles, setSelectedFiles] = useState([]);
  
  const [serviceType, setServiceType] = useState('');
  const [serviceTypeFields, setServiceTypeFields] = useState({});
  const [validationError, setValidationError] = useState(null);
  const [student, setStudent] = useState('');
  const [selectedTherapist, setSelectedTherapist] = useState('');
  const queryClient = useQueryClient();
  const { token, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const dateInputRef = useRef(null);

  // Pre-select student if navigated from My Students
  useEffect(() => {
    if (location.state?.studentId) {
      setStudent(String(location.state.studentId));
    }
  }, [location.state]);

  // Check if user is manager or admin
  const isManagerOrAdmin = user?.role === 'manager' || user?.role === 'admin';

  // Fetch therapist's students (existing logic)
  const { data: studentsData, isLoading: loadingStudents } = useQuery({
    queryKey: ['my-students'],
    queryFn: async () => {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/therapists/my-students`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch students');
      return response.json();
    },
  });

  // Fetch all therapists (for managers/admins only)
  const { data: therapistsData, isLoading: loadingTherapists } = useQuery({
    queryKey: ['therapists-for-reports'],
    queryFn: async () => {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/therapists`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch therapists');
      return response.json();
    },
    enabled: isManagerOrAdmin,
  });

  // Fetch students based on selected therapist (for managers/admins)
  const { data: therapistStudentsData, isLoading: loadingTherapistStudents } = useQuery({
    queryKey: ['therapist-students', selectedTherapist],
    queryFn: async () => {
      if (!selectedTherapist) return { students: [] };
      
      if (selectedTherapist === 'all') {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/students/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Failed to fetch all students');
        return response.json();
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/therapists/${selectedTherapist}/students`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch therapist students');
      return response.json();
    },
    enabled: isManagerOrAdmin && !!selectedTherapist,
  });

  // Default therapist selection to current user (managers/admins are typically reporting for themselves)
  useEffect(() => {
    if (isManagerOrAdmin && therapistsData?.therapists && user?.userId && !selectedTherapist) {
      const selfTherapist = therapistsData.therapists.find(
        (t) => String(t.id) === String(user.userId)
      );
      if (selfTherapist) {
        setSelectedTherapist(String(selfTherapist.id));
      }
    }
  }, [isManagerOrAdmin, therapistsData, user?.userId]);

  // Determine which students to show
  const getAvailableStudents = () => {
    if (isManagerOrAdmin && selectedTherapist) {
      return therapistStudentsData?.students || [];
    }
    return studentsData?.students || [];
  };

  const createReport = useMutation({
    mutationFn: async (formData) => {
      // Bound the request so a stalled upload surfaces a clear error instead of
      // hanging until the browser's own (long, opaque) timeout. Aborting also
      // closes the connection, which lets the server free the stuck worker.
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/session-reports`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`
          },
          credentials: 'include',
          body: formData,
          signal: controller.signal
        });
        if (!response.ok) {
          // The body may not be JSON (e.g. a 502 from a killed worker), so fall
          // back to a status-based message rather than throwing on parse.
          let message = 'Failed to create report';
          try {
            const error = await response.json();
            message = error.msg || message;
          } catch {
            message = `Server error (${response.status}). Please try again.`;
          }
          throw new Error(message);
        }
        return response.json();
      } catch (err) {
        if (err.name === 'AbortError') {
          throw new Error(
            'The submission timed out. It may not have been saved — please check '
            + 'your reports list before resubmitting.'
          );
        }
        if (err instanceof TypeError) {
          // "Failed to fetch": the request never completed at the network layer.
          throw new Error(
            'Network error while submitting. Please check your reports list '
            + 'before resubmitting to avoid duplicates.'
          );
        }
        throw err;
      } finally {
        clearTimeout(timeoutId);
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['session-reports']);
      resetForm();

      // Build success message
      let successMessage = 'Session report created successfully.';
      if (data.extractionResults) {
        if (data.extractionResults.failed > 0) {
          successMessage += ` Document Processing: ${data.extractionResults.successful} successful, ${data.extractionResults.failed} failed. Administrators have been notified of any failures.`;
        } else if (data.extractionResults.successful > 0) {
          successMessage += ` ${data.extractionResults.successful} document(s) successfully processed.`;
        }
      }
      if (data.therapistName && isManagerOrAdmin) {
        successMessage += ` Report created for: ${data.therapistName}.`;
      }

      // Redirect to view page with success message
      navigate('/dashboard/reports/view', { state: { successMessage } });
    },
    onError: (error) => {
      setValidationError(error.message);
    }
  });

  // Get all files based on service type
  const getAllFiles = () => {
    switch (serviceType) {
      case 'Initial Assessment':
        const iaFiles = [];
        if (assessmentFile) iaFiles.push(assessmentFile);
        if (memoFile) iaFiles.push(memoFile);
        return iaFiles;
      case '12 Week Review':
        return reviewFile ? [reviewFile] : [];
      default:
        return selectedFiles;
    }
  };

  const validateFiles = () => {
    switch (serviceType) {
      case 'Initial Assessment':
        if (!assessmentFile) {
          return 'Initial Assessment requires an Initial Assessment form';
        }
        if (!memoFile) {
          return 'Initial Assessment requires a Student Memo';
        }
        break;
      case '12 Week Review':
        if (!reviewFile) {
          return '12 Week Review requires a 12 Week Review form';
        }
        break;
      case 'Songwriting Assessment':
        if (selectedFiles.length < 1) {
          return 'Songwriting Assessment requires at least one assessment form';
        }
        break;
      case 'Session':
        if (selectedFiles.length < 1) {
          return 'Session requires at least one file (session notes)';
        }
        break;
      default:
        if (selectedFiles.length === 0) {
          return 'At least one file is required';
        }
    }
    return null;
  };

  const resetForm = () => {
    setSelectedFiles([]);
    setAssessmentFile(null);
    setMemoFile(null);
    setReviewFile(null);
    setServiceType('');
    setServiceTypeFields({});
    setStudent('');
    setSelectedTherapist('');
    setValidationError(null);
  };

  // Reset file state and extra fields when service type changes
  const handleServiceTypeChange = (newServiceType) => {
    setServiceType(newServiceType);
    setSelectedFiles([]);
    setAssessmentFile(null);
    setMemoFile(null);
    setReviewFile(null);
    setValidationError(null);
    setServiceTypeFields(defaultServiceTypeFields[newServiceType] || {});
  };

  const handleServiceTypeFieldChange = (field, value) => {
    setServiceTypeFields(prev => ({ ...prev, [field]: value }));
  };

  const validateServiceTypeFields = () => {
    if (serviceType === 'Initial Assessment') {
      if (!serviceTypeFields.requestedSemesterActivities) return 'Requested Semester Activities is required';
      if (!serviceTypeFields.instrumentChosen?.trim()) return 'Instrument Chosen is required';
    }
    if (serviceType === 'Songwriting Assessment') {
      if (!serviceTypeFields.songwriterGenderPreference) return 'Songwriter Gender Preference is required';
      if (!serviceTypeFields.preferredGenre?.trim()) return 'Preferred Genre is required';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Single-flight: ignore extra submits while one is already in flight, so a
    // double-click can't fire two requests with two different idempotency keys.
    if (createReport.isLoading) {
      return;
    }

    const fileError = validateFiles();
    if (fileError) {
      setValidationError(fileError);
      return;
    }

    const fieldError = validateServiceTypeFields();
    if (fieldError) {
      setValidationError(fieldError);
      return;
    }

    // Create FormData object
    const formData = new FormData();

    // Add form fields to FormData
    formData.append('studentId', e.target.studentId.value);
    formData.append('serviceType', serviceType);
    formData.append('date', e.target.date.value);
    formData.append('sessionLength', e.target.sessionLength.value);
    formData.append('placeOfService', e.target.placeOfService.value);

    // Per-submit idempotency key. A transport-level retry of this request
    // reuses the same key, so the backend can recognise it and avoid creating
    // a duplicate report.
    const idempotencyKey =
      (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    formData.append('idempotencyKey', idempotencyKey);

    // Add therapist ID if manager/admin and therapist is selected
    if (isManagerOrAdmin && selectedTherapist && selectedTherapist !== 'all') {
      formData.append('therapistId', selectedTherapist);
    }

    // Append service-type-specific fields as JSON blob
    formData.append('serviceTypeFields', JSON.stringify(serviceTypeFields));

    // Get all files for the current service type
    const allFiles = getAllFiles();

    // Add each file and its type to FormData
    allFiles.forEach((fileItem) => {
      formData.append('files[]', fileItem.file);
      formData.append('fileTypes[]', fileItem.type);
    });

    createReport.mutate(formData);
  };

  const handleTherapistChange = (e) => {
    setSelectedTherapist(e.target.value);
    setStudent(''); // Reset student selection when therapist changes
  };

  // Render the appropriate upload component based on service type
  const renderFileUpload = () => {
    switch (serviceType) {
      case 'Initial Assessment':
        return (
          <InitialAssessmentUpload
            assessmentFile={assessmentFile}
            memoFile={memoFile}
            onAssessmentChange={setAssessmentFile}
            onMemoChange={setMemoFile}
          />
        );
      case '12 Week Review':
        return (
          <TwelveWeekReviewUpload
            reviewFile={reviewFile}
            onReviewChange={setReviewFile}
          />
        );
      default:
        return (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Documentation Files
              <span className="text-red-500">*</span>
            </label>
            <FileUploadWithType 
              files={selectedFiles}
              onFilesChange={setSelectedFiles}
              serviceType={serviceType}
            />
          </div>
        );
    }
  };

  // Render service-type-specific extra fields
  const renderServiceTypeFields = () => {
    switch (serviceType) {
      case 'Initial Assessment':
        return <InitialAssessmentFields values={serviceTypeFields} onChange={handleServiceTypeFieldChange} />;
      case '12 Week Review':
        return <TwelveWeekReviewFields values={serviceTypeFields} onChange={handleServiceTypeFieldChange} />;
      case 'Songwriting Assessment':
        return <SongwritingAssessmentFields values={serviceTypeFields} onChange={handleServiceTypeFieldChange} />;
      case 'Session':
        return <SessionFields values={serviceTypeFields} onChange={handleServiceTypeFieldChange} />;
      default:
        return null;
    }
  };

  if (loadingStudents || (isManagerOrAdmin && loadingTherapists)) {
    return <div className="flex justify-center p-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Create Session Report</h2>
        <p className="mt-1 text-sm text-slate-500">Enter details for a new session</p>
      </div>

      {location.state?.studentName && (
        <Alert className="bg-green-50 border-green-200">
          <AlertDescription className="text-green-800">
            Student pre-selected: <strong>{location.state.studentName}</strong>
          </AlertDescription>
        </Alert>
      )}

      {validationError && (
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{validationError}</AlertDescription>
        </Alert>
      )}

      <Card className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Therapist Selection - Only show for managers/admins */}
          {isManagerOrAdmin && (
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Select Therapist
                <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedTherapist}
                onChange={handleTherapistChange}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2"
                required
              >
                <option value="">Select a therapist</option>
                <option value="all">All Students (No specific therapist)</option>
                {therapistsData?.therapists.map((therapist) => (
                  <option key={therapist.id} value={therapist.id}>
                    {therapist.firstName} {therapist.lastName}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-slate-500">
                Select the therapist this session report is for. Choose "All Students" to select from any student.
              </p>
            </div>
          )}

          {/* Student Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-700">
              Student
              <span className="text-red-500">*</span>
            </label>
            <select
              name="studentId"
              value={student}
              onChange={(e) => setStudent(e.target.value)}
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2"
              required
              disabled={isManagerOrAdmin && !selectedTherapist}
            >
              <option value="">
                {isManagerOrAdmin && !selectedTherapist 
                  ? 'Select a therapist first' 
                  : 'Select a student'
                }
              </option>
              {getAvailableStudents().map((s) => (
                <option key={s.id} value={s.id}>
                  {s.firstName} {s.lastName}
                </option>
              ))}
            </select>
            {isManagerOrAdmin && !selectedTherapist && (
              <p className="mt-1 text-xs text-red-500">
                Please select a therapist first to see available students
              </p>
            )}
            {loadingTherapistStudents && (
              <p className="mt-1 text-xs text-slate-500">Loading students...</p>
            )}
          </div>

          {/* Service Type */}
          <div>
            <label className="block text-sm font-medium text-slate-700">
              Service Type
              <span className="text-red-500">*</span>
            </label>
            <select
              name="serviceType"
              value={serviceType}
              onChange={(e) => handleServiceTypeChange(e.target.value)}
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2"
              required
            >
              <option value="">Select service type</option>
              {Object.values(SERVICE_TYPES).map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          {/* Session Date */}
          <div>
            <label className="block text-sm font-medium text-slate-700">
              Session Date
              <span className="text-red-500">*</span>
            </label>
            <div className="mt-1 flex rounded-md shadow-sm">
              <span
                className="inline-flex items-center rounded-l-md border border-r-0 border-slate-300 bg-slate-50 px-3 text-slate-500 cursor-pointer hover:bg-slate-100"
                onClick={() => dateInputRef.current?.showPicker()}
                title="Open date picker"
              >
                <Calendar className="h-4 w-4" />
              </span>
              <input
                ref={dateInputRef}
                type="date"
                name="date"
                className="block w-full rounded-none rounded-r-md border border-slate-300 px-3 py-2"
                required
              />
            </div>
          </div>

          {/* Session Length */}
          <div>
            <label className="block text-sm font-medium text-slate-700">
              Session Length
              <span className="text-red-500">*</span>
            </label>
            <select
              name="sessionLength"
              defaultValue="60"
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2"
              required
            >
              <option value="">Select session length</option>
              {SESSION_LENGTHS.map((length) => (
                <option key={length.value} value={length.value}>
                  {length.label}
                </option>
              ))}
            </select>
          </div>

          {/* Place of Service */}
          <div>
            <label className="block text-sm font-medium text-slate-700">
              Place of Service
              <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="placeOfService"
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2"
              required
              placeholder="e.g., Client's home, Office, Virtual session"
            />
          </div>

          {/* File Upload - Conditional based on service type */}
          {serviceType && (
            <>
              {renderServiceTypeFields()}
              <div className="border-t border-slate-200 pt-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-4">Documentation</p>
                {renderFileUpload()}
              </div>
            </>
          )}

          {/* Show prompt to select service type if not selected */}
          {!serviceType && (
            <div className="bg-slate-50 border border-slate-200 rounded-md p-4 text-center">
              <p className="text-sm text-slate-600">
                Please select a service type to see additional fields and required documentation.
              </p>
            </div>
          )}

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={resetForm}
              className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-600"
            >
              Reset Form
            </button>
            <button
              type="submit"
              disabled={createReport.isLoading || (isManagerOrAdmin && !selectedTherapist)}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createReport.isLoading ? 'Submitting...' : 'Create Report'}
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
};

// PaymentStatus component
const PaymentStatus = ({ reportId, isPaid, paidAt, paidBy, onStatusChange }) => {
  const { user } = useAuth();
  const isManagerOrAdmin = user?.role === 'manager' || user?.role === 'admin';
  const [isHovered, setIsHovered] = useState(false);

  const handleToggle = async () => {
    if (!isManagerOrAdmin) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/session-reports/${reportId}/payment-status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('wmf_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ isPaid: !isPaid })
      });

      if (!response.ok) {
        throw new Error('Failed to update payment status');
      }

      const data = await response.json();
      onStatusChange(data);
    } catch (error) {
      console.error('Error updating payment status:', error);
      alert('Failed to update payment status');
    }
  };

  if (isPaid) {
    return (
      <div 
        className={`flex items-center space-x-1 ${isManagerOrAdmin ? 'cursor-pointer' : ''}`}
        onClick={handleToggle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        title={isManagerOrAdmin ? 'Click to mark as unpaid' : `Paid on ${new Date(paidAt).toLocaleDateString("en-us", { timeZone: 'UTC' })} by ${paidBy}`}
      >
        <Check className={`h-4 w-4 ${isHovered && isManagerOrAdmin ? 'text-yellow-500' : 'text-green-500'}`} />
        <span className={`text-sm ${isHovered && isManagerOrAdmin ? 'text-yellow-600' : 'text-green-600'}`}>
          {isHovered && isManagerOrAdmin ? 'Mark Unpaid' : 'Paid'}
        </span>
      </div>
    );
  }

  return (
    <div 
      className={`flex items-center space-x-1 ${isManagerOrAdmin ? 'cursor-pointer' : ''}`}
      onClick={handleToggle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      title={isManagerOrAdmin ? 'Click to mark as paid' : 'Unpaid'}
    >
      <span className={`h-2 w-2 rounded-full ${isHovered && isManagerOrAdmin ? 'bg-green-500' : 'bg-yellow-500'}`} />
      <span className={`text-sm ${isHovered && isManagerOrAdmin ? 'text-green-600' : 'text-yellow-600'}`}>
        {isHovered && isManagerOrAdmin ? 'Mark Paid' : 'Pending'}
      </span>
    </div>
  );
};

// ReviewStatus component   visible to admins and managers only
const ReviewStatus = ({ reportId, isReviewed, reviewedAt, reviewedBy, onStatusChange }) => {
  const { user } = useAuth();
  const isManagerOrAdmin = user?.role === 'manager' || user?.role === 'admin';
  const [isHovered, setIsHovered] = useState(false);

  const handleToggle = async () => {
    if (!isManagerOrAdmin) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/session-reports/${reportId}/review-status`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('wmf_token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ isReviewed: !isReviewed }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update review status');
      }

      const data = await response.json();
      onStatusChange(data);
    } catch (error) {
      console.error('Error updating review status:', error);
      alert('Failed to update review status');
    }
  };

  if (isReviewed) {
    return (
      <div
        className={`flex items-center space-x-1 ${isManagerOrAdmin ? 'cursor-pointer' : ''}`}
        onClick={handleToggle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        title={
          isManagerOrAdmin
            ? 'Click to mark as pending'
            : `Reviewed on ${new Date(reviewedAt).toLocaleDateString("en-us", { timeZone: 'UTC' })} by ${reviewedBy}`
        }
      >
        <Check className={`h-4 w-4 ${isHovered && isManagerOrAdmin ? 'text-yellow-500' : 'text-blue-500'}`} />
        <span className={`text-sm ${isHovered && isManagerOrAdmin ? 'text-yellow-600' : 'text-blue-600'}`}>
          {isHovered && isManagerOrAdmin ? 'Mark Pending' : 'Done'}
        </span>
      </div>
    );
  }

  return (
    <div
      className={`flex items-center space-x-1 ${isManagerOrAdmin ? 'cursor-pointer' : ''}`}
      onClick={handleToggle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      title={isManagerOrAdmin ? 'Click to mark as done' : 'Pending review'}
    >
      <span className={`h-2 w-2 rounded-full ${isHovered && isManagerOrAdmin ? 'bg-blue-500' : 'bg-slate-400'}`} />
      <span className={`text-sm ${isHovered && isManagerOrAdmin ? 'text-blue-600' : 'text-slate-500'}`}>
        {isHovered && isManagerOrAdmin ? 'Mark Done' : 'Pending'}
      </span>
    </div>
  );
};

const DocumentPreviewModal = ({ doc, token, onClose }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isPdf = doc.fileType === 'application/pdf' || doc.fileName.toLowerCase().endsWith('.pdf');
  const isWord =
    (doc.fileType && doc.fileType.toLowerCase().includes('word')) ||
    doc.fileName.toLowerCase().endsWith('.docx') ||
    doc.fileName.toLowerCase().endsWith('.doc');

  useEffect(() => {
    const loadPreview = async () => {
      try {
        // Step 1: exchange the main JWT for a short-lived, document-scoped preview token
        const tokenResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/session-reports/documents/${doc.id}/preview-token`,
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (!tokenResponse.ok) throw new Error('Failed to obtain preview token');
        const { previewToken } = await tokenResponse.json();

        if (isPdf) {
          // Fetch the PDF and turn it into a same-origin blob: URL. This
          // detaches rendering from the API response headers (nosniff /
          // framing) that cause Edge to block a framed cross-origin PDF.
          // Rendered via <object>/<embed> (no sandbox) below.
          const docResponse = await fetch(
            `${process.env.REACT_APP_API_URL}/session-reports/documents/${doc.id}/preview?token=${encodeURIComponent(previewToken)}`
          );
          if (!docResponse.ok) throw new Error('Failed to load document');
          const blob = await docResponse.blob();
          // Force the PDF MIME type so the browser viewer is used regardless
          // of how the fetched blob's type was reported.
          const pdfBlob = blob.type === 'application/pdf'
            ? blob
            : new Blob([blob], { type: 'application/pdf' });
          setPreviewUrl(window.URL.createObjectURL(pdfBlob));
        } else {
          // Word docs are served as HTML, which Edge frames without issue.
          setPreviewUrl(
            `${process.env.REACT_APP_API_URL}/session-reports/documents/${doc.id}/preview?token=${encodeURIComponent(previewToken)}`
          );
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadPreview();

    // Revoke blob URL on unmount (PDF only)
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        window.URL.revokeObjectURL(previewUrl);
      }
    };
  }, [doc.id, token]);

  const renderPreview = () => {
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center h-full space-y-3 text-slate-500">
          <div className="h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm">Loading document...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center h-full space-y-2 text-red-500">
          <p className="text-sm font-medium">Failed to load preview</p>
          <p className="text-xs text-slate-500">{error}</p>
        </div>
      );
    }

    if (previewUrl) {
      if (isPdf) {
        // <object>/<embed> (no sandbox) render the same-origin blob via the
        // browser's native PDF viewer. Edge blocks PDFs in sandboxed/framed
        // cross-origin contexts, but not this one.
        return (
          <object
            data={previewUrl}
            type="application/pdf"
            title={doc.fileName}
            className="w-full h-full rounded border-0"
          >
            <embed src={previewUrl} type="application/pdf" className="w-full h-full" />
            <div className="flex flex-col items-center justify-center h-full space-y-2 text-slate-500">
              <p className="text-sm">Unable to display the PDF inline.</p>
              <a href={previewUrl} target="_blank" rel="noopener noreferrer"
                 className="text-blue-600 underline text-sm">
                Open in a new tab
              </a>
            </div>
          </object>
        );
      }
      return (
        <iframe
          src={previewUrl}
          title={doc.fileName}
          className="w-full h-full rounded border border-slate-200 bg-white"
          sandbox="allow-same-origin"
        />
      );
    }
    return null;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl mx-4 flex flex-col" style={{ height: '90vh' }}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 flex-shrink-0">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900 truncate">{doc.fileName}</p>
            {doc.fileTypeCategory && (
              <p className="text-xs text-slate-500">{doc.fileTypeCategory}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="ml-4 flex-shrink-0 text-slate-400 hover:text-slate-700 p-1 rounded hover:bg-slate-100"
            title="Close preview"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-hidden p-4">
          {renderPreview()}
        </div>
      </div>
    </div>
  );
};

// Session Report Details Modal
const SessionReportDetails = ({ report, onClose, showTherapist }) => {
  const { token, user } = useAuth();
  const [downloading, setDownloading] = useState({});
  const [previewDoc, setPreviewDoc] = useState(null);
  const isManagerOrAdmin = user?.role === 'manager' || user?.role === 'admin' || user?.role === 'Manager' || user?.role === 'Admin';
  const canPreviewDocuments = isManagerOrAdmin || (user?.role && user.role.toLowerCase() !== 'therapist');

  const handleDownload = async (docId, filename) => {
    setDownloading(prev => ({ ...prev, [docId]: true }));
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/session-reports/documents/${docId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download document');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Failed to download document');
    } finally {
      setDownloading(prev => ({ ...prev, [docId]: false }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <h3 className="text-lg font-semibold text-slate-900">Session Report Details</h3>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-slate-500">Date</p>
                <p className="text-sm text-slate-900">{new Date(report.date).toLocaleDateString("en-us", { timeZone: 'UTC' })}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">Student</p>
                <p className="text-sm text-slate-900">{report.studentName}</p>
              </div>
              {showTherapist && (
                <div>
                  <p className="text-sm font-medium text-slate-500">Therapist</p>
                  <p className="text-sm text-slate-900">{report.therapistName}</p>
                </div>
              )}
              <div>
                <p className="text-sm font-medium text-slate-500">Service Type</p>
                <p className="text-sm text-slate-900">{report.serviceType}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">Session Length</p>
                <p className="text-sm text-slate-900">{report.sessionLength} minutes</p>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">Place of Service</p>
                <p className="text-sm text-slate-900">{report.placeOfService}</p>
              </div>

              {/* Service-type-specific fields */}
              {report.requestedSemesterActivities && (
                <div><p className="text-sm font-medium text-slate-500">Semester Activities</p><p className="text-sm text-slate-900">{report.requestedSemesterActivities}</p></div>
              )}
              {report.instrumentChosen && (
                <div><p className="text-sm font-medium text-slate-500">Instrument Chosen</p><p className="text-sm text-slate-900">{report.instrumentChosen}</p></div>
              )}
              {report.studentNeedsInstrument != null && report.serviceType === 'Initial Assessment' && (
                <div><p className="text-sm font-medium text-slate-500">Needs Instrument</p><p className="text-sm text-slate-900">{report.studentNeedsInstrument ? 'Yes' : 'No'}</p></div>
              )}
              {report.nextSemesterLessons && (
                <div><p className="text-sm font-medium text-slate-500">Next Semester (Lessons)</p><p className="text-sm text-slate-900">{report.nextSemesterLessons}</p></div>
              )}
              {report.nextSemesterTherapy && (
                <div><p className="text-sm font-medium text-slate-500">Next Semester (Therapy)</p><p className="text-sm text-slate-900">{report.nextSemesterTherapy}</p></div>
              )}
              {report.changeInstruments != null && report.serviceType === '12 Week Review' && (
                <div><p className="text-sm font-medium text-slate-500">Change Instruments</p><p className="text-sm text-slate-900">{report.changeInstruments ? 'Yes' : 'No'}</p></div>
              )}
              {report.newInstrument && (
                <div><p className="text-sm font-medium text-slate-500">New Instrument</p><p className="text-sm text-slate-900">{report.newInstrument}</p></div>
              )}
              {report.requestThirdSemester != null && report.serviceType === '12 Week Review' && (
                <div><p className="text-sm font-medium text-slate-500">Request Third Semester</p><p className={`text-sm font-medium ${report.requestThirdSemester ? 'text-purple-600' : 'text-slate-900'}`}>{report.requestThirdSemester ? 'Yes — Manager Notified' : 'No'}</p></div>
              )}
              {report.songwriterGenderPreference && (
                <div><p className="text-sm font-medium text-slate-500">Songwriter Gender Pref.</p><p className="text-sm text-slate-900">{report.songwriterGenderPreference}</p></div>
              )}
              {report.preferredGenre && (
                <div><p className="text-sm font-medium text-slate-500">Preferred Genre</p><p className="text-sm text-slate-900">{report.preferredGenre}</p></div>
              )}
              {report.noCallNoShow != null && report.serviceType === 'Session' && (
                <div><p className="text-sm font-medium text-slate-500">No Call / No Show</p><p className={`text-sm font-medium ${report.noCallNoShow ? 'text-red-600' : 'text-green-600'}`}>{report.noCallNoShow ? 'Yes' : 'No'}</p></div>
              )}
            </div>

            {report.documents && report.documents.length > 0 && (
              <div>
                <p className="text-sm font-medium text-slate-500 mb-2">Documents</p>
                <div className="space-y-2">
                  {report.documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-md"
                    >
                      <div className="flex-1 min-w-0">
                        {canPreviewDocuments ? (
                          <button
                            onClick={() => setPreviewDoc(doc)}
                            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline truncate text-left"
                            title="Click to preview document"
                          >
                            {doc.fileName}
                          </button>
                        ) : (
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {doc.fileName}
                          </p>
                        )}
                        {doc.fileTypeCategory && (
                          <p className="text-xs text-slate-500">
                            Type: {doc.fileTypeCategory}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 ml-4 flex-shrink-0">
                        {canPreviewDocuments && (
                          <button
                            onClick={() => setPreviewDoc(doc)}
                            className="flex items-center space-x-1 text-xs text-slate-500 hover:text-blue-600 px-2 py-1 rounded hover:bg-slate-200"
                            title="Preview document"
                          >
                            <Eye className="h-3.5 w-3.5" />
                            <span>Preview</span>
                          </button>
                        )}
                        <button
                          onClick={() => handleDownload(doc.id, doc.fileName)}
                          disabled={downloading[doc.id]}
                          className="text-blue-600 hover:text-blue-800 disabled:opacity-50"
                          title="Download document"
                        >
                          {downloading[doc.id] ? (
                            <span className="text-xs">Downloading...</span>
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="rounded-md bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {previewDoc && (
        <DocumentPreviewModal
          doc={previewDoc}
          token={token}
          onClose={() => setPreviewDoc(null)}
        />
      )}
    </div>
  );
};

// View Session Reports component
const ViewSessionReports = () => {
  const { token, user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const isManagerOrAdmin = user?.role === 'manager' || user?.role === 'admin';

  const [successMessage, setSuccessMessage] = useState(location.state?.successMessage || null);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [selectedStudent, setSelectedStudent] = useState('');
  const [selectedTherapist, setSelectedTherapist] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);
  const [hideReviewed, setHideReviewed] = useState(false);
  const [unpaidOnly, setUnpaidOnly] = useState(false);
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  const handleTherapistChange = (therapistId) => {
    setSelectedTherapist(therapistId);
    setSelectedStudent('');
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // Fetch students — for managers, scoped to selected therapist when one is chosen
  const { data: studentsData } = useQuery({
    queryKey: isManagerOrAdmin ? ['therapist-students', selectedTherapist] : ['my-students'],
    queryFn: async () => {
      const url = isManagerOrAdmin && selectedTherapist
        ? `${process.env.REACT_APP_API_URL}/therapists/${selectedTherapist}/students`
        : `${process.env.REACT_APP_API_URL}/therapists/my-students`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch students');
      return response.json();
    },
  });

  // Fetch session reports
  const { data: reportsData, isLoading } = useQuery({
    queryKey: ['session-reports', dateRange, selectedStudent, selectedTherapist, hideReviewed, unpaidOnly],
    queryFn: async () => {
      let url = `${process.env.REACT_APP_API_URL}/session-reports/list?`;
      if (dateRange.start) url += `start=${dateRange.start}&`;
      if (dateRange.end) url += `end=${dateRange.end}&`;
      if (selectedStudent) url += `studentId=${selectedStudent}&`;
      if (selectedTherapist) url += `therapistId=${selectedTherapist}&`;
      if (hideReviewed) url += `hideReviewed=true&`;
      if (unpaidOnly) url += `unpaidOnly=true&`;

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch reports');
      return response.json();
    },
  });

  // Fetch all therapists (for managers/admins)
  const { data: therapistsData } = useQuery({
    queryKey: ['therapists'],
    queryFn: async () => {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/therapists`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch therapists');
      return response.json();
    },
    enabled: isManagerOrAdmin,
  });

  if (isLoading) {
    return <div className="flex justify-center p-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Session Reports</h2>
          <p className="mt-1 text-sm text-slate-500">View and manage session reports</p>
        </div>
        <button
          onClick={() => navigate('/dashboard/reports/create')}
          className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          <ClipboardList className="h-4 w-4" />
          Create Session Report
        </button>
      </div>

      {successMessage && (
        <Alert className="bg-green-50 border-green-200">
          <Check className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800 flex items-center justify-between">
            <span>{successMessage}</span>
            <button
              onClick={() => setSuccessMessage(null)}
              className="ml-4 text-green-600 hover:text-green-800"
            >
              <X className="h-4 w-4" />
            </button>
          </AlertDescription>
        </Alert>
      )}

      <Card className="p-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="block text-sm font-medium text-slate-700">Start Date</label>
            <div className="mt-1 flex rounded-md shadow-sm">
              <span className="inline-flex items-center rounded-l-md border border-r-0 border-slate-300 px-3 text-slate-500">
                <Calendar className="h-4 w-4" />
              </span>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="block w-full rounded-none rounded-r-md border border-slate-300 px-3 py-2"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700">End Date</label>
            <div className="mt-1 flex rounded-md shadow-sm">
              <span className="inline-flex items-center rounded-l-md border border-r-0 border-slate-300 px-3 text-slate-500">
                <Calendar className="h-4 w-4" />
              </span>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="block w-full rounded-none rounded-r-md border border-slate-300 px-3 py-2"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700">Student</label>
            <div className="mt-1 flex rounded-md shadow-sm">
              <span className="inline-flex items-center rounded-l-md border border-r-0 border-slate-300 px-3 text-slate-500">
                <Users className="h-4 w-4" />
              </span>
              <select
                value={selectedStudent}
                onChange={(e) => setSelectedStudent(e.target.value)}
                className="block w-full rounded-none rounded-r-md border border-slate-300 px-3 py-2"
              >
                <option value="">All Students</option>
                {studentsData?.students.map((student) => (
                  <option key={student.id} value={student.id}>
                    {student.firstName} {student.lastName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {isManagerOrAdmin && (
            <div>
              <label className="block text-sm font-medium text-slate-700">Therapist</label>
              <div className="mt-1 flex rounded-md shadow-sm">
                <span className="inline-flex items-center rounded-l-md border border-r-0 border-slate-300 px-3 text-slate-500">
                  <UserCircle className="h-4 w-4" />
                </span>
                <select
                  value={selectedTherapist}
                  onChange={(e) => handleTherapistChange(e.target.value)}
                  className="block w-full rounded-none rounded-r-md border border-slate-300 px-3 py-2"
                >
                  <option value="">All Therapists</option>
                  {therapistsData?.therapists.map((therapist) => (
                    <option key={therapist.id} value={therapist.id}>
                      {therapist.firstName} {therapist.lastName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
        {isManagerOrAdmin && (
          <div className="mt-4 flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={hideReviewed}
                onChange={(e) => setHideReviewed(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-blue-600"
              />
              Hide reviewed
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={unpaidOnly}
                onChange={(e) => setUnpaidOnly(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-blue-600"
              />
              Unpaid only
            </label>
          </div>
        )}
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer select-none hover:text-slate-700"
                  onClick={() => handleSort('date')}
                >
                  Date {sortField === 'date' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer select-none hover:text-slate-700"
                  onClick={() => handleSort('studentName')}
                >
                  Student {sortField === 'studentName' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                </th>
                {isManagerOrAdmin && (
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer select-none hover:text-slate-700"
                    onClick={() => handleSort('therapistName')}
                  >
                    Therapist {sortField === 'therapistName' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Service Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Length
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Payment Status
                </th>
                {isManagerOrAdmin && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Review Status
                  </th>
                )}
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {[...(reportsData?.reports ?? [])].sort((a, b) => {
                let aVal = sortField === 'date' ? a.date : a[sortField] ?? '';
                let bVal = sortField === 'date' ? b.date : b[sortField] ?? '';
                const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
                return sortDir === 'asc' ? cmp : -cmp;
              }).map((report) => (
                <tr key={report.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                    {new Date(report.date).toLocaleDateString("en-us", { timeZone: 'UTC' })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                    {report.studentName}
                  </td>
                  {isManagerOrAdmin && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      {report.therapistName}
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                    <span className="inline-flex items-center gap-1">
                      {report.serviceType}
                      {report.noCallNoShow && (
                        <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">NCNS</span>
                      )}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                    {report.sessionLength} minutes
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                    <PaymentStatus 
                      reportId={report.id}
                      isPaid={report.paymentStatus}
                      paidAt={report.paidAt}
                      paidBy={report.paidBy}
                      onStatusChange={(data) => {
                        const updatedReports = reportsData.reports.map(r => 
                          r.id === report.id 
                            ? { 
                                ...r, 
                                paymentStatus: data.paymentStatus,
                                paidAt: data.paidAt,
                                paidBy: data.paidBy
                              } 
                            : r
                        );
                        queryClient.setQueryData(['session-reports', dateRange, selectedStudent, selectedTherapist, hideReviewed, unpaidOnly], {
                          ...reportsData,
                          reports: updatedReports
                        });
                      }}
                    />
                  </td>
                  {isManagerOrAdmin && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      <ReviewStatus
                        reportId={report.id}
                        isReviewed={report.reviewStatus}
                        reviewedAt={report.reviewedAt}
                        reviewedBy={report.reviewedBy}
                        onStatusChange={(data) => {
                          const updatedReports = reportsData.reports.map(r =>
                            r.id === report.id
                              ? {
                                  ...r,
                                  reviewStatus: data.reviewStatus,
                                  reviewedAt: data.reviewedAt,
                                  reviewedBy: data.reviewedBy
                                }
                              : r
                          );
                          queryClient.setQueryData(['session-reports', dateRange, selectedStudent, selectedTherapist, hideReviewed, unpaidOnly], {
                            ...reportsData,
                            reports: updatedReports
                          });
                        }}
                      />
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => setSelectedReport(report)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {selectedReport && (
        <SessionReportDetails
          report={selectedReport}
          onClose={() => setSelectedReport(null)}
          showTherapist={isManagerOrAdmin}
        />
      )}
    </div>
  );
};

// Export the components as expected by your existing application
export const SessionReports = {
  Create: CreateSessionReport,
  View: ViewSessionReports,
};

export default SessionReports;
